import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { 
    Plus, HardHat, Trash2, ChevronRight, Loader2, AlertTriangle, 
    XCircle, CheckCircle2, Clock, Settings, Lock, RotateCcw
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { chantiersAPI } from '@/lib/api';
import { useAuth } from '@/lib/auth';

const STAGES = [
    { id: 'ch_formulaire_rempli', name: 'Formulaire rempli', color: 'bg-slate-100 text-slate-700' },
    { id: 'ch_visite_technique', name: 'Visite technique', color: 'bg-blue-100 text-blue-700' },
    { id: 'ch_devis_envoye', name: 'Devis envoyé', color: 'bg-amber-100 text-amber-700' },
    { id: 'ch_devis_signe', name: 'Devis signé', color: 'bg-emerald-100 text-emerald-700' },
    { id: 'ch_dossier_subvention', name: 'Dossier subvention', color: 'bg-purple-100 text-purple-700' },
    { id: 'ch_subvention_obtenue', name: 'Subvention obtenue', color: 'bg-teal-100 text-teal-700' },
    { id: 'ch_chantier_en_cours', name: 'Chantier en cours', color: 'bg-orange-100 text-orange-700' },
    { id: 'ch_chantier_termine', name: 'Chantier terminé', color: 'bg-cyan-100 text-cyan-700' },
    { id: 'ch_gagne', name: 'Gagné', color: 'bg-green-100 text-green-700' },
    { id: 'ch_perdu', name: 'Perdu', color: 'bg-red-100 text-red-700' },
];

const formatCurrency = (v) => {
    if (!v && v !== 0) return '—';
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(v);
};

export default function ChantiersTabs({ prospectId, onUpdate, isAuditorView = false }) {
    const { user } = useAuth();
    const [chantiers, setChantiers] = useState([]);
    const [activeTab, setActiveTab] = useState(null);
    const [loading, setLoading] = useState(true);
    const [newDialog, setNewDialog] = useState(false);
    const [newName, setNewName] = useState('');
    const [saving, setSaving] = useState(false);
    const [lossDialog, setLossDialog] = useState({ open: false, chantierId: null });
    const [lossReason, setLossReason] = useState('');
    const [lossComment, setLossComment] = useState('');
    
    // Auditeur : ne voit AUCUNE commission
    const isAdmin = !isAuditorView && (user?.role === 'admin_full' || user?.role === 'admin' || user?.is_admin === true);
    const isVP = !isAuditorView && user?.role === 'vp';

    const loadChantiers = useCallback(async () => {
        try {
            const res = await chantiersAPI.list(prospectId);
            setChantiers(res.data?.chantiers || []);
            if (!activeTab && res.data?.chantiers?.length > 0) {
                setActiveTab(res.data.chantiers[0].id);
            }
        } catch {
            // Pas de chantiers
        } finally {
            setLoading(false);
        }
    }, [prospectId, activeTab]);

    useEffect(() => { loadChantiers(); }, [loadChantiers]);

    const handleCreate = async () => {
        if (!newName.trim()) return;
        setSaving(true);
        try {
            const res = await chantiersAPI.create(prospectId, { name: newName.trim() });
            toast.success(`Chantier "${newName}" créé`);
            setNewDialog(false);
            setNewName('');
            await loadChantiers();
            if (res.data?.chantier?.id) setActiveTab(res.data.chantier.id);
            onUpdate?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur');
        } finally {
            setSaving(false);
        }
    };

    const handleStageChange = async (chantierId, newStage) => {
        if (newStage === 'ch_perdu') {
            setLossDialog({ open: true, chantierId });
            return;
        }
        try {
            await chantiersAPI.update(prospectId, chantierId, { stage: newStage });
            toast.success('Étape mise à jour');
            await loadChantiers();
            onUpdate?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur');
        }
    };

    const handleConfirmLoss = async () => {
        if (!lossReason.trim()) { toast.error('Raison obligatoire'); return; }
        setSaving(true);
        try {
            await chantiersAPI.update(prospectId, lossDialog.chantierId, {
                stage: 'ch_perdu',
                loss_reason: lossReason,
                loss_comment: lossComment
            });
            toast.success('Chantier marqué comme perdu');
            setLossDialog({ open: false, chantierId: null });
            setLossReason('');
            setLossComment('');
            await loadChantiers();
            onUpdate?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur');
        } finally {
            setSaving(false);
        }
    };

    const handleCommissionUpdate = async (chantierId, field, value) => {
        try {
            await chantiersAPI.update(prospectId, chantierId, { [field]: parseFloat(value) || 0 });
            await loadChantiers();
            onUpdate?.();
        } catch {
            toast.error('Erreur mise à jour');
        }
    };

    const handleResetChantierCommission = async (chantierId) => {
        try {
            await chantiersAPI.update(prospectId, chantierId, { commission_manual_override: false });
            toast.success('Chantier revenu au calcul automatique');
            await loadChantiers();
            onUpdate?.();
        } catch {
            toast.error('Erreur');
        }
    };

    const handleDelete = async (chantierId) => {
        if (!confirm('Supprimer ce chantier ?')) return;
        try {
            await chantiersAPI.delete(prospectId, chantierId);
            toast.success('Chantier supprimé');
            setActiveTab(null);
            await loadChantiers();
            onUpdate?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur');
        }
    };

    const activeCh = chantiers.find(ch => ch.id === activeTab);
    const stageInfo = STAGES.find(s => s.id === activeCh?.stage) || STAGES[0];

    // Synthèse globale
    const synthesis = {
        total: chantiers.reduce((s, ch) => s + (ch.commission_totale || ch.commission_ht || 0), 0),
        total_vp: chantiers.reduce((s, ch) => s + (ch.commission_vp_payeur_nette || 0), 0),
        total_app: chantiers.reduce((s, ch) => s + (ch.commission_apporteur_1 || 0), 0),
        total_valnexo: chantiers.reduce((s, ch) => s + (ch.restant_valnexo || ch.commission_valnexo || 0), 0),
        total_at: chantiers.reduce((s, ch) => s + (ch.restant_at_service || ch.commission_at_service || 0), 0),
    };

    if (loading) return <Card><CardContent className="py-6"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></CardContent></Card>;

    return (
        <Card data-testid="chantiers-tabs">
            <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                    <CardTitle className="text-base flex items-center gap-2">
                        <HardHat className="w-5 h-5 text-blue-600" />
                        Chantiers
                        <Badge variant="secondary" className="ml-1">{chantiers.length}</Badge>
                    </CardTitle>
                    <Button size="sm" onClick={() => setNewDialog(true)} className="gap-1.5 h-8" data-testid="add-chantier-btn">
                        <Plus className="w-3.5 h-3.5" />
                        Ajouter
                    </Button>
                </div>

                {/* Synthèse globale */}
                {chantiers.length > 1 && (synthesis.total > 0 || synthesis.total_vp > 0) && (
                    <div className="mt-2 p-3 bg-slate-50 rounded-lg border border-slate-200">
                        <p className="text-xs font-semibold text-slate-600 mb-2">Synthèse tous chantiers :</p>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                            {isAdmin && synthesis.total > 0 && (
                                <div className="text-center p-1.5 bg-white rounded border">
                                    <p className="font-bold text-slate-800">{formatCurrency(synthesis.total)}</p>
                                    <p className="text-slate-500">Total</p>
                                </div>
                            )}
                            {(isAdmin || isVP) && synthesis.total_vp > 0 && (
                                <div className="text-center p-1.5 bg-purple-50 rounded border border-purple-200">
                                    <p className="font-bold text-purple-700">{formatCurrency(synthesis.total_vp)}</p>
                                    <p className="text-purple-500">VP</p>
                                </div>
                            )}
                            {synthesis.total_app > 0 && (
                                <div className="text-center p-1.5 bg-orange-50 rounded border border-orange-200">
                                    <p className="font-bold text-orange-700">{formatCurrency(synthesis.total_app)}</p>
                                    <p className="text-orange-500">Apporteur</p>
                                </div>
                            )}
                            {isAdmin && synthesis.total_valnexo > 0 && (
                                <div className="text-center p-1.5 bg-green-50 rounded border border-green-200">
                                    <p className="font-bold text-green-700">{formatCurrency(synthesis.total_valnexo)}</p>
                                    <p className="text-green-500">Valnexo</p>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </CardHeader>

            <CardContent className="pt-0">
                {chantiers.length === 0 ? (
                    <div className="text-center py-6 text-sm text-slate-500">
                        <HardHat className="w-10 h-10 mx-auto text-slate-300 mb-2" />
                        Aucun chantier. Cliquez "Ajouter" pour créer le premier.
                    </div>
                ) : (
                    <>
                        {/* Tabs */}
                        <div className="flex gap-1 overflow-x-auto pb-2 mb-3 border-b border-slate-100">
                            {chantiers.map(ch => {
                                const st = STAGES.find(s => s.id === ch.stage);
                                return (
                                    <button
                                        key={ch.id}
                                        onClick={() => setActiveTab(ch.id)}
                                        className={cn(
                                            "flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all shrink-0",
                                            activeTab === ch.id 
                                                ? "bg-slate-900 text-white shadow-sm" 
                                                : "text-slate-600 hover:bg-slate-100"
                                        )}
                                        data-testid={`chantier-tab-${ch.id}`}
                                    >
                                        <HardHat className="w-3.5 h-3.5" />
                                        {ch.name}
                                        {ch.stage === 'ch_gagne' && <CheckCircle2 className="w-3 h-3 text-green-400" />}
                                        {ch.stage === 'ch_perdu' && <XCircle className="w-3 h-3 text-red-400" />}
                                    </button>
                                );
                            })}
                        </div>

                        {/* Active chantier detail */}
                        {activeCh && (
                            <div key={activeCh.id} className="space-y-4" data-testid={`chantier-detail-${activeCh.id}`}>
                                {/* Stage + Actions */}
                                <div className="flex items-center justify-between gap-2 flex-wrap">
                                    <Badge className={cn("text-xs", stageInfo.color)}>
                                        {stageInfo.name}
                                    </Badge>
                                    <div className="flex items-center gap-2">
                                        <Select value={activeCh.stage} onValueChange={(v) => handleStageChange(activeCh.id, v)}>
                                            <SelectTrigger className="h-8 text-xs w-44" data-testid="chantier-stage-select">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {STAGES.map(s => (
                                                    <SelectItem key={s.id} value={s.id} disabled={s.id === activeCh.stage}>
                                                        {s.name}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        {isAdmin && (
                                            <Button variant="ghost" size="sm" onClick={() => handleDelete(activeCh.id)} className="text-red-500 h-8 w-8 p-0">
                                                <Trash2 className="w-4 h-4" />
                                            </Button>
                                        )}
                                    </div>
                                </div>

                                {/* Loss reason */}
                                {activeCh.stage === 'ch_perdu' && activeCh.loss_reason && (
                                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                                        <div className="flex items-center gap-2 font-medium"><XCircle className="w-4 h-4" /> Perdu</div>
                                        <p className="mt-1">Raison : {activeCh.loss_reason}</p>
                                        {activeCh.loss_comment && <p className="italic mt-1">"{activeCh.loss_comment}"</p>}
                                    </div>
                                )}

                                {/* Commissions */}
                                {isAdmin && (
                                    <div className="space-y-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
                                        <h4 className="text-sm font-medium text-slate-700 flex items-center gap-2">
                                            <Settings className="w-4 h-4" />
                                            Commissions — {activeCh.name}
                                        </h4>
                                        
                                        {/* Commission Totale — champ principal */}
                                        <div className="space-y-1">
                                            <Label className="text-xs font-semibold text-slate-800">Commission Totale HT</Label>
                                            <Input
                                                type="number"
                                                step="0.01"
                                                className="h-9 text-sm font-semibold border-slate-300"
                                                defaultValue={activeCh.commission_totale || ''}
                                                onBlur={(e) => handleCommissionUpdate(activeCh.id, 'commission_totale', e.target.value)}
                                                placeholder="Saisissez la commission totale..."
                                                data-testid="chantier-commission-totale"
                                            />
                                            <p className="text-[10px] text-slate-400">
                                                {activeCh.commission_manual_override
                                                    ? "Répartition figée manuellement. Modifier le total ne réécrase pas les montants."
                                                    : "La répartition se calcule automatiquement depuis ce montant"}
                                            </p>
                                        </div>

                                        {/* Bandeau override manuel + retour auto */}
                                        {activeCh.commission_totale > 0 && activeCh.commission_manual_override && (
                                            <div className="flex items-center justify-between gap-2 p-2 bg-amber-50 border border-amber-200 rounded-lg" data-testid="chantier-manual-override-banner">
                                                <span className="flex items-center gap-1.5 text-[11px] font-medium text-amber-700">
                                                    <Lock className="w-3.5 h-3.5" />
                                                    Répartition manuelle (verrouillée)
                                                </span>
                                                <Button
                                                    variant="outline" size="sm"
                                                    className="h-7 text-[11px] text-amber-700 border-amber-300 hover:bg-amber-100"
                                                    onClick={() => handleResetChantierCommission(activeCh.id)}
                                                    data-testid="chantier-reset-auto-btn"
                                                >
                                                    <RotateCcw className="w-3 h-3 mr-1" />
                                                    Revenir au calcul auto
                                                </Button>
                                            </div>
                                        )}
                                        
                                        {/* Répartition calculée (modifiable) */}
                                        {(activeCh.commission_totale > 0) && (
                                            <div className="grid grid-cols-2 gap-2 pt-1">
                                                <div className="p-2.5 bg-purple-50 border border-purple-200 rounded-lg space-y-1">
                                                    <Label className="text-[10px] text-purple-600 font-semibold">Commission VP</Label>
                                                    <Input
                                                        type="number" step="0.01" className="h-7 text-xs border-purple-200"
                                                        defaultValue={activeCh.commission_vp_payeur_nette || ''}
                                                        onBlur={(e) => handleCommissionUpdate(activeCh.id, 'commission_vp_payeur_nette', e.target.value)}
                                                        data-testid="chantier-vp-input"
                                                    />
                                                </div>
                                                <div className="p-2.5 bg-orange-50 border border-orange-200 rounded-lg space-y-1">
                                                    <Label className="text-[10px] text-orange-600 font-semibold">Commission Apporteur</Label>
                                                    <Input
                                                        type="number" step="0.01" className="h-7 text-xs border-orange-200"
                                                        defaultValue={activeCh.commission_apporteur_1 || ''}
                                                        onBlur={(e) => handleCommissionUpdate(activeCh.id, 'commission_apporteur_1', e.target.value)}
                                                        data-testid="chantier-app-input"
                                                    />
                                                </div>
                                                <div className="p-2.5 bg-green-50 border border-green-200 rounded-lg space-y-1">
                                                    <Label className="text-[10px] text-green-600 font-semibold">Restant Valnexo</Label>
                                                    <Input
                                                        type="number" step="0.01" className="h-7 text-xs border-green-200"
                                                        defaultValue={activeCh.restant_valnexo || activeCh.commission_valnexo || ''}
                                                        onBlur={(e) => handleCommissionUpdate(activeCh.id, 'restant_valnexo', e.target.value)}
                                                        data-testid="chantier-valnexo-input"
                                                    />
                                                </div>
                                                <div className="p-2.5 bg-blue-50 border border-blue-200 rounded-lg space-y-1">
                                                    <Label className="text-[10px] text-blue-600 font-semibold">Restant AT Service (solde auto)</Label>
                                                    <Input
                                                        type="number" step="0.01" className="h-7 text-xs border-blue-200 bg-blue-100/40"
                                                        value={activeCh.restant_at_service ?? activeCh.commission_at_service ?? ''}
                                                        disabled
                                                        readOnly
                                                        data-testid="chantier-at-display"
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* VP view: only their commission */}
                                {isVP && !isAdmin && (
                                    <div className="space-y-2">
                                        {activeCh.commission_vp_payeur_nette > 0 && (
                                            <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg flex justify-between items-center">
                                                <span className="text-sm text-purple-700 font-medium">Ma commission VP</span>
                                                <span className="text-lg font-bold text-purple-700">{formatCurrency(activeCh.commission_vp_payeur_nette)}</span>
                                            </div>
                                        )}
                                        {activeCh.commission_apporteur_1 > 0 && (
                                            <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg flex justify-between items-center">
                                                <span className="text-sm text-orange-700 font-medium">Commission Apporteur</span>
                                                <span className="text-lg font-bold text-orange-700">{formatCurrency(activeCh.commission_apporteur_1)}</span>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Apporteur view */}
                                {!isVP && !isAdmin && (
                                    activeCh.commission_apporteur_1 > 0 ? (
                                        <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg flex justify-between items-center">
                                            <span className="text-sm text-emerald-700 font-medium">Votre commission</span>
                                            <span className="text-lg font-bold text-emerald-700">{formatCurrency(activeCh.commission_apporteur_1)}</span>
                                        </div>
                                    ) : null
                                )}
                            </div>
                        )}
                    </>
                )}
            </CardContent>

            {/* Dialog nouveau chantier */}
            <Dialog open={newDialog} onOpenChange={setNewDialog}>
                <DialogContent className="max-w-sm">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <HardHat className="w-5 h-5 text-blue-600" />
                            Nouveau chantier
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3 py-2">
                        <Label>Nom du chantier</Label>
                        <Input
                            value={newName}
                            onChange={(e) => setNewName(e.target.value)}
                            placeholder="Ex: Toiture, Pompe à chaleur, Isolation..."
                            onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
                            data-testid="new-chantier-name"
                        />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setNewDialog(false)}>Annuler</Button>
                        <Button onClick={handleCreate} disabled={!newName.trim() || saving} className="gap-1.5">
                            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                            Créer
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Dialog perte */}
            <Dialog open={lossDialog.open} onOpenChange={(o) => { if (!o) setLossDialog({ open: false, chantierId: null }); }}>
                <DialogContent className="max-w-sm">
                    <DialogHeader>
                        <DialogTitle className="text-red-600 flex items-center gap-2">
                            <AlertTriangle className="w-5 h-5" />
                            Marquer comme perdu
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3 py-2">
                        <div>
                            <Label>Raison <span className="text-red-500">*</span></Label>
                            <Input value={lossReason} onChange={(e) => setLossReason(e.target.value)} placeholder="Ex: Budget, concurrent..." data-testid="chantier-loss-reason" />
                        </div>
                        <div>
                            <Label>Commentaire</Label>
                            <Textarea value={lossComment} onChange={(e) => setLossComment(e.target.value)} placeholder="Détails..." rows={2} />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setLossDialog({ open: false, chantierId: null })}>Annuler</Button>
                        <Button variant="destructive" onClick={handleConfirmLoss} disabled={!lossReason.trim() || saving}>
                            {saving && <Loader2 className="w-4 h-4 animate-spin mr-1" />}
                            Confirmer
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Card>
    );
}
